/**
 * Recruiter Evaluation Page
 *
 * View and manage evaluation sessions and candidate decisions.
 */

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  useListEvaluationSessionsQuery,
  useGetEvaluationSessionQuery,
  useCreateEvaluationSessionMutation,
  useUpdateEvaluationDecisionMutation,
  useCompleteEvaluationSessionMutation,
  EvaluationDecision,
  EvaluationSessionStatus,
} from '../store/api/recruiterApi';

export function RecruiterEvaluationPage() {
  const { sessionId } = useParams<{ sessionId?: string }>();
  const [showNewModal, setShowNewModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<EvaluationSessionStatus | ''>('');

  // If sessionId is present, show session detail
  if (sessionId && sessionId !== 'new') {
    return <EvaluationSessionDetail sessionId={sessionId} />;
  }

  // Otherwise show sessions list
  return (
    <EvaluationSessionsList
      statusFilter={statusFilter}
      onStatusFilterChange={setStatusFilter}
      showNewModal={showNewModal || sessionId === 'new'}
      onShowNewModal={setShowNewModal}
    />
  );
}

interface EvaluationSessionsListProps {
  statusFilter: EvaluationSessionStatus | '';
  onStatusFilterChange: (status: EvaluationSessionStatus | '') => void;
  showNewModal: boolean;
  onShowNewModal: (show: boolean) => void;
}

function EvaluationSessionsList({
  statusFilter,
  onStatusFilterChange,
  showNewModal,
  onShowNewModal,
}: EvaluationSessionsListProps) {
  const { data, isLoading } = useListEvaluationSessionsQuery({
    status: statusFilter || undefined,
    limit: 50,
  });
  const [createSession, { isLoading: isCreating }] = useCreateEvaluationSessionMutation();

  const handleCreateSession = async (title: string, description: string) => {
    try {
      await createSession({ title, description }).unwrap();
      onShowNewModal(false);
    } catch (err) {
      console.error('Failed to create session:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Evaluation Sessions</h1>
          <p className="mt-1 text-gray-500">
            Review candidates and track your decisions
          </p>
        </div>
        <button onClick={() => onShowNewModal(true)} className="btn btn-primary">
          New Session
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <select
          value={statusFilter}
          onChange={(e) => onStatusFilterChange(e.target.value as EvaluationSessionStatus | '')}
          className="input w-auto"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Sessions List */}
      <div className="card">
        <div className="card-body">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : data && data.items.length > 0 ? (
            <div className="space-y-4">
              {data.items.map((session) => (
                <Link
                  key={session.id}
                  to={`/recruiter/evaluations/${session.id}`}
                  className="block border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900">
                          {session.title || 'Untitled Session'}
                        </h3>
                        <StatusBadge status={session.status} />
                      </div>
                      {session.description && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                          {session.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <span>{session.itemCount} candidates</span>
                        <span>{session.totalCreditsUsed} credits used</span>
                        <span>Created {new Date(session.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <ClipboardIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No evaluation sessions yet</p>
              <p className="text-sm text-gray-400 mt-1">
                Create a session to start reviewing candidates
              </p>
              <button
                onClick={() => onShowNewModal(true)}
                className="btn btn-primary mt-4"
              >
                Create Session
              </button>
            </div>
          )}
        </div>
      </div>

      {/* New Session Modal */}
      {showNewModal && (
        <NewSessionModal
          onClose={() => onShowNewModal(false)}
          onSubmit={handleCreateSession}
          isLoading={isCreating}
        />
      )}
    </div>
  );
}

interface EvaluationSessionDetailProps {
  sessionId: string;
}

function EvaluationSessionDetail({ sessionId }: EvaluationSessionDetailProps) {
  const { data, isLoading, error } = useGetEvaluationSessionQuery(sessionId);
  const [updateDecision, { isLoading: isUpdating }] = useUpdateEvaluationDecisionMutation();
  const [completeSession, { isLoading: isCompleting }] = useCompleteEvaluationSessionMutation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">Failed to load evaluation session</p>
        <Link to="/recruiter/evaluations" className="btn btn-outline mt-4">
          Back to Sessions
        </Link>
      </div>
    );
  }

  const { session, items } = data;

  const handleDecisionUpdate = async (itemId: string, decision: EvaluationDecision) => {
    try {
      await updateDecision({ itemId, decision }).unwrap();
    } catch (err) {
      console.error('Failed to update decision:', err);
    }
  };

  const handleComplete = async () => {
    if (confirm('Complete this evaluation session? This action cannot be undone.')) {
      try {
        await completeSession(sessionId).unwrap();
      } catch (err) {
        console.error('Failed to complete session:', err);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Link
            to="/recruiter/evaluations"
            className="text-sm text-primary-600 hover:underline mb-2 inline-block"
          >
            ← Back to Sessions
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            {session.title || 'Untitled Session'}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <StatusBadge status={session.status} />
            <span className="text-gray-500">
              {session.itemCount} candidates • {session.totalCreditsUsed} credits
            </span>
          </div>
          {session.description && (
            <p className="text-gray-600 mt-2">{session.description}</p>
          )}
        </div>
        {session.status === 'active' && (
          <button
            onClick={handleComplete}
            disabled={isCompleting}
            className="btn btn-outline"
          >
            Complete Session
          </button>
        )}
      </div>

      {/* Candidates Grid */}
      <div className="card">
        <div className="card-body">
          {items.length > 0 ? (
            <div className="space-y-4">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="border rounded-lg p-4"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-gray-900">
                        Candidate {item.candidateUserId.slice(0, 8)}...
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {item.creditsUsed} credit used •{' '}
                        Added {new Date(item.createdAt).toLocaleDateString()}
                      </p>
                      {item.notes && (
                        <p className="text-sm text-gray-600 mt-2">{item.notes}</p>
                      )}
                    </div>

                    {/* Decision Buttons */}
                    {session.status === 'active' ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleDecisionUpdate(item.id, 'interested')}
                          disabled={isUpdating}
                          className={`btn btn-sm ${
                            item.decision === 'interested'
                              ? 'btn-primary'
                              : 'btn-outline text-green-600 border-green-200 hover:bg-green-50'
                          }`}
                        >
                          Interested
                        </button>
                        <button
                          onClick={() => handleDecisionUpdate(item.id, 'maybe')}
                          disabled={isUpdating}
                          className={`btn btn-sm ${
                            item.decision === 'maybe'
                              ? 'btn-primary'
                              : 'btn-outline text-yellow-600 border-yellow-200 hover:bg-yellow-50'
                          }`}
                        >
                          Maybe
                        </button>
                        <button
                          onClick={() => handleDecisionUpdate(item.id, 'not_interested')}
                          disabled={isUpdating}
                          className={`btn btn-sm ${
                            item.decision === 'not_interested'
                              ? 'btn-primary'
                              : 'btn-outline text-red-600 border-red-200 hover:bg-red-50'
                          }`}
                        >
                          Not Interested
                        </button>
                      </div>
                    ) : (
                      <DecisionBadge decision={item.decision} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">No candidates in this session yet</p>
              {/* No web flow adds a candidate to an evaluation yet; the old
                  "Add Candidates" button led to the Marketplace, which has no
                  such action. Say so instead of promising one. */}
              <p className="text-sm text-gray-400 mt-1" data-testid="evaluation-add-not-available">
                Adding candidates to an evaluation is not available in the web app yet.
              </p>
              <Link to="/marketplace" className="btn btn-outline mt-4">
                Browse Marketplace
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface NewSessionModalProps {
  onClose: () => void;
  onSubmit: (title: string, description: string) => void;
  isLoading: boolean;
}

function NewSessionModal({ onClose, onSubmit, isLoading }: NewSessionModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">New Evaluation Session</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title (optional)
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Q1 Sales Hiring"
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Notes about this evaluation..."
              rows={3}
              className="input w-full"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} disabled={isLoading} className="btn btn-outline">
            Cancel
          </button>
          <button
            onClick={() => onSubmit(title, description)}
            disabled={isLoading}
            className="btn btn-primary"
          >
            {isLoading ? 'Creating...' : 'Create Session'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    completed: 'bg-blue-100 text-blue-800',
    cancelled: 'bg-gray-100 text-gray-800',
  };

  return (
    <span className={`px-2 py-0.5 text-xs rounded-full ${colors[status] || colors.active}`}>
      {status}
    </span>
  );
}

function DecisionBadge({ decision }: { decision: string }) {
  const colors: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-800',
    interested: 'bg-green-100 text-green-800',
    maybe: 'bg-yellow-100 text-yellow-800',
    not_interested: 'bg-red-100 text-red-800',
  };

  const labels: Record<string, string> = {
    pending: 'Pending',
    interested: 'Interested',
    maybe: 'Maybe',
    not_interested: 'Not Interested',
  };

  return (
    <span className={`px-2 py-1 text-sm rounded-full ${colors[decision] || colors.pending}`}>
      {labels[decision] || decision}
    </span>
  );
}

// Icons
function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function ClipboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  );
}
